import React, { useState } from 'react';
import Tabs from '@ui/tabs/Tabs';
import Legend from '@ui/legend/Legend';
import { BookingPositionCell, BookingPositionState } from '../model/BookingPositionState';
import {
	cellRankValue,
	dayLabel,
	formatCount,
	gridCellText,
	HEAD_SLOTS,
	mainHotel,
	monthLabel,
	nightLabel,
	ordinal,
	percentText,
	POSITION_TIERS,
	positionTier,
	sameCityHotels,
	tierLabel,
	whole,
} from '../BookingRanking.utilities';
import { HotelNameCell, RankingCard, RankingCardHead } from '../BookingRankingParts';

// Feature-local Screen Part (user decision of 2026-10-05): one box per hotel and night, on the colour of
// the position tier (rankTierOf of the design system v1.5.0, the classes of RankTierChip).
export const PositionBox: React.FunctionComponent<{ cell: BookingPositionCell }> = ({ cell }) => {
	const text = gridCellText(cell);
	const tier = positionTier(cellRankValue(cell));
	if (cell.state === 'SOLD_OUT' || cell.state === 'MIN_STAY') {
		return <div title={text.title} className="flex h-16 flex-col items-center justify-start rounded-md bg-gray-100 px-1 py-1.5 text-center text-gray-700">
			<span className="text-xs font-semibold leading-5">{text.main}</span>
		</div>;
	}
	if (cell.state === 'NOT_FOUND') {
		return <div title={text.title} className="flex h-16 flex-col items-center justify-start rounded-md border border-dashed border-gray-500 px-1 py-1 text-center text-gray-600">
			<span className="text-xs font-semibold leading-5">{text.main}</span>
			<span className="whitespace-nowrap text-xs">{text.sub}</span>
		</div>;
	}
	return <div title={text.title} className={`flex h-16 flex-col items-center justify-start rounded-md px-1 py-1 text-center ${tier?.className ?? ''}`}>
		<span className={cell.state === 'POSITION' ? 'text-base font-bold leading-5' : 'whitespace-nowrap text-xs font-semibold leading-5'}>{text.main}</span>
		<span className="whitespace-nowrap text-xs leading-4">{text.sub}</span>
		<span className="mt-0.5 w-full whitespace-nowrap border-t border-slate-500/25 pt-0.5 text-xs leading-4">{text.price}</span>
	</div>;
};

export const TierLegend: React.FunctionComponent<{ withNightStates?: boolean }> = ({ withNightStates = false }) =>
	<Legend groups={[{
		label: 'Position',
		items: [
			...POSITION_TIERS.map((tier) => ({ label: tierLabel(tier), mark: 'square' as const, markClassName: tier.className })),
			...(withNightStates ? [
				{ label: 'Sold out', mark: 'square' as const, markClassName: 'bg-gray-100 ring-1 ring-inset ring-gray-300' },
				{ label: 'Not found', mark: 'square' as const, markClassName: 'bg-white ring-1 ring-inset ring-gray-500' },
			] : []),
		],
	}]} />;

// Night by night: month tabs over all scanned nights of the month; each box = the latest check of the
// night (D11). The grid scrolls sideways with a sticky hotel column.
const NightGrid: React.FunctionComponent<{ state: BookingPositionState }> = ({ state }) => {
	const [month, setMonth] = useState<string>(state.months[0]?.month ?? '');
	const hotels = sameCityHotels(state);
	const main = mainHotel(state);
	const indexes = state.nights.map((n, i) => ({ n, i })).filter(({ n }) => n.date.startsWith(month));
	const m = main?.months.find((x) => x.month === month);
	const scan = state.scan;
	const example = main?.cells.find((c) => c.date.startsWith(month) && c.state === 'POSITION' && c.cityDearerShare !== null && c.compsetPriceRank !== null);

	return <RankingCard>
		<RankingCardHead title="Night by night"
			subtitle={`The latest check of each night · all ${indexes.length} scanned nights of ${monthLabel(month, 'long')}`} />
		<div className="px-4 pt-3 sm:px-5">
			<Tabs options={state.months.map((x) => ({ label: monthLabel(x.month), value: x.month }))} value={month} onChange={(value) => setMonth(String(value))} />
		</div>
		{m !== undefined && m.typical !== null && <p className="px-4 pt-3 text-sm text-gray-700 sm:px-5">
			<span className="font-semibold">{monthLabel(month, 'longYear')}</span> for you: typical position {whole(m.typical)}
			{m.compsetRank !== null && m.compsetOf !== null && `, ${ordinal(m.compsetRank)} of ${m.compsetOf} in your compset`}
			{m.cityDearerShare !== null && `, dearer than ${percentText(m.cityDearerShare)} of the city`}
			{m.compsetPriceRank !== null && m.compsetPriceOf !== null && `, ${ordinal(m.compsetPriceRank)} cheapest of ${m.compsetPriceOf} on a typical night`}.
			{' '}Sold out on {m.soldOut} of {m.nights} nights.
		</p>}
		<div className="px-4 pt-2.5 sm:px-5"><TierLegend withNightStates /></div>
		<p className="px-4 pt-1.5 text-xs leading-5 text-gray-600 sm:px-5">
			Each box shows the latest check of the night.
			{scan?.window === 'FIRST_SCAN' && ` In this first scan (${dayLabel(scan.scanDate)}) each night has ${scan.checksPerNight} checks: the box shows their middle position, then the best and worst check (or how many checks found the hotel).`}
			{example !== undefined && ` Under the line: ${percentText(example.cityDearerShare)} · ${ordinal(example.compsetPriceRank as number)} = dearer than ${percentText(example.cityDearerShare)} of the city, ${ordinal(example.compsetPriceRank as number)} cheapest in your compset.`}
			{` Grey ("Not in 500") = the middle of the checks is not in the top 500; a check that does not find the hotel in the first ${HEAD_SLOTS} results counts as not in the top 500. Price not seen = no check found the hotel, so there is no price.`}
		</p>
		<div className="mt-3 overflow-x-auto pb-4">
			<table className="border-separate border-spacing-x-1 border-spacing-y-1.5">
				<thead><tr>
					<th className="sticky left-0 z-10 w-32 bg-white px-4 sm:w-72 text-left align-bottom text-xs font-normal text-gray-500 sm:px-5">Hotel</th>
					{indexes.map(({ n }) => <th key={n.date} className="w-24 whitespace-nowrap text-center text-xs font-semibold text-gray-700">
						{nightLabel(n.date)}
						{n.onSaleCount !== null && <span className="block font-normal text-gray-500">{formatCount(n.onSaleCount)} on sale</span>}
					</th>)}
				</tr></thead>
				<tbody>
					{hotels.map((hotel) => <tr key={hotel.hotelId} className={hotel.isMain ? 'bg-sky-50' : ''}>
						<td className={`sticky left-0 z-10 w-32 px-4 sm:w-72 sm:px-5 ${hotel.isMain ? 'border-l-4 border-blue-600 bg-sky-50' : 'bg-white'}`}>
							<span className="block w-28 sm:w-64"><HotelNameCell hotel={hotel} /></span>
						</td>
						{indexes.map(({ n, i }) => <td key={n.date} className="w-24">
							<span className="block w-24"><PositionBox cell={hotel.cells[i]} /></span>
						</td>)}
					</tr>)}
				</tbody>
			</table>
		</div>
	</RankingCard>;
};

export default NightGrid;
